import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import * as reducers from 'Store/reducers';
import { sendAnalyticsEventById, viewedModelPage } from 'Store/stats/actions';
import apps from 'Settings/apps';

import GamePlayer from 'Containers/GamePlayer';
import GameTopics from 'Components/GameTopics';
import GamePrintables from 'Components/GamePrintables';
import GameObjectives from 'Components/GameObjectives';
import GameBreadcrumbs from 'Containers/GameBreadcrumbs';
import GameQuestionsSection from 'Components/GameQuestionsSection';
import SectionGroup from 'Components/SectionGroup';
import SectionHeader from 'Components/SectionHeader';
import ScrollToBanner from 'Containers/ScrollToBanner';
import PromotionalBanner from 'Components/PromotionalBanner';
import DownArrowHint from 'Components/DownArrowHint';
import ConnectedFormattedMessage from 'Containers/ConnectedFormattedMessage';
import Error from 'Containers/Error';
import Loading from 'Components/Loading';
import InfoBanner from 'Components/InfoBanner';
import { hasSpecialAccess } from 'Utils/specialAccess';
import Link from 'Components/Link';
import { ROUTES } from 'Settings/route-strings';

class PlayerPage extends React.Component {
  constructor(props) {
    super(props);

    this.gameExists = Object.keys(apps).includes(props.match.params.gameSlug);

    this.state = {
      scrollToPx: null,
      sentViewAnalytics: false,
    };
  }

  componentDidMount() {
    if (this.gameExists) {
      this.setState({
        scrollToPx: this.scrollToAnchor ? this.scrollToAnchor.offsetTop : 0,
      });
      this.sendAnalytics();
    }
  }

  componentDidUpdate() {
    if (this.gameExists) this.sendAnalytics();
  }

  sendAnalytics() {
    if (this.props.activeUser && !this.state.sentViewAnalytics) {
      this.setState({ sentViewAnalytics: true }, () =>
        this.props.viewedModelPage(this.props.match.params.gameSlug)
      );
    }
    if (this.props.activeUser && !this.props.seenWelcomeBanner)
      this.props.sendAnalyticsEventById(
        'onboarding_welcome_banner',
        'viewed model'
      );
  }

  render() {
    if (!this.gameExists) {
      return <Error type="pageNotFound" code="404" />;
    }

    const {
      location,
      activeUser,
      activeSchool,
      seenResourcesHint,
      match: { params },
      objectiveMessageIds,
    } = this.props;

    const { disabledModules, classDisabledModules } = activeSchool || {};

    if (
      !hasSpecialAccess(location) &&
      (!activeUser || !disabledModules || !classDisabledModules)
    )
      return <Loading />;

    const isDisabled =
      (disabledModules || []).includes(params.gameSlug) ||
      classDisabledModules.includes(params.gameSlug);
    const { scrollToPx } = this.state;
    const isEducator = activeUser ? activeUser.isEducator : false;
    const isAdmin = activeUser ? activeUser.isAdmin : false;

    if (activeUser && activeUser.isStudent && isDisabled) {
      return <Error type="modelDisabled" code="404" />;
    }

    return (
      <main className="Page__content">
        {isDisabled && isEducator && (
          <InfoBanner>
            <span style={{ paddingRight: 4 }}>
              This module is disabled for students.
            </span>
            {isAdmin ? (
              <span>
                Enable it on the <Link to={ROUTES.EXPLORE}>Explore page</Link>
              </span>
            ) : (
              <span>Please contact your admin to enable it.</span>
            )}
          </InfoBanner>
        )}
        <ScrollToBanner
          scrollToPx={scrollToPx}
          hintId="player_teaching_resources"
          bannerIsReady={isEducator && typeof scrollToPx === 'number'}
          render={(data) => (
            <PromotionalBanner
              type="explore-model"
              visible={seenResourcesHint === false}
              headline={
                <ConnectedFormattedMessage id="promotional_banner.explore-model.headline" />
              }
              description={
                <ConnectedFormattedMessage id="promotional_banner.explore-model.description" />
              }
              closeHandler={() =>
                this.props.sendAnalyticsEventById(
                  'player_teaching_resources',
                  'clicked close'
                )
              }
              ctaButton={
                <DownArrowHint
                  clickHandler={data.handleUIHintClick}
                  style={{
                    position: 'absolute',
                    bottom: '-2.5em',
                    left: 'calc(50% - 2.5em)',
                  }}
                />
              }
            />
          )}
        />
        <GameBreadcrumbs promoBannerIsVisible={!seenResourcesHint} />
        <GamePlayer />
        <SectionGroup additionalClasses=" SectionGroup--game-player-info">
          <GameQuestionsSection slug={params.gameSlug} />
          <GameTopics />
        </SectionGroup>
        <a ref={(a) => (this.scrollToAnchor = a)} />
        {isEducator ? (
          <SectionGroup additionalClasses=" SectionGroup--game-resources">
            <SectionHeader
              type="teaching-resources"
              title={
                <ConnectedFormattedMessage id="teaching_resources.title" />
              }
            />
            <div className="SectionGroup__column SectionGroup__column--goals">
              <GameObjectives
                slug={params.gameSlug}
                messageIds={objectiveMessageIds}
              />
            </div>
            <div className="SectionGroup__column SectionGroup__column--printables">
              <GamePrintables />
            </div>
          </SectionGroup>
        ) : null}
      </main>
    );
  }
}

const mapStateToProps = (state, { match: { params } }) => {
  return {
    activeUser: reducers.getActiveUser(state),
    activeSchool: reducers.getActiveSchool(state),
    seenResourcesHint: reducers.getHasUIHintBeenSeen(
      state,
      'player_teaching_resources'
    ),
    seenWelcomeBanner: reducers.getHasUIHintBeenSeen(
      state,
      'onboarding_welcome_banner'
    ),
    objectiveMessageIds: reducers.getObjectiveMessageIdsByModel(
      state,
      params.gameSlug
    ),
    questionMessageIds: reducers.getQuestionMessageIdsByModel(
      state,
      params.gameSlug
    ),
  };
};

export default withRouter(
  connect(mapStateToProps, {
    sendAnalyticsEventById,
    viewedModelPage,
  })(PlayerPage)
);
